import { SuppliesReorderTriggerType } from "@redotech/crdb-enums/product/supplies-reorder-trigger-type";
import { AdvancedFilterInputSchema } from "@redotech/redo-fulfillment-common/fulfillment-group-summary-input";
import {
  suppliesInventoryConfigIdFormat,
  suppliesInventoryReorderIdFormat,
  suppliesSubscriptionIdFormat,
} from "@redotech/redo-marketplace-id";
import {
  productFamilyIdFormat,
  productIdFormat,
} from "@redotech/redo-product-id";
import { OpensearchPaginationCursorSchema } from "@redotech/redo-views-common/table";
import * as z from "zod";
import { SuppliesInventoryConfigRowSchema } from "./get-supplies-inventory-configs";

/**
 * Something else on the same order. `anchor` is true when it was due on its own,
 * which is why the order shipped when it did.
 */
const ConsolidatedSiblingSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("config"),
    id: suppliesInventoryConfigIdFormat.schema,
    productTitle: z.string(),
    anchor: z.boolean(),
  }),
  z.object({
    kind: z.literal("subscription"),
    id: suppliesSubscriptionIdFormat.schema,
    productTitle: z.string(),
    // Cycles are never pulled early, so a subscription on the order is always
    // part of why it shipped.
    anchor: z.literal(true),
  }),
]);

export type ConsolidatedSibling = z.infer<typeof ConsolidatedSiblingSchema>;

const SuppliesInventoryReorderRowSchema = z.object({
  reorderId: suppliesInventoryReorderIdFormat.schema,
  // Mongo Order ObjectId (hex) of the order placed for this reorder.
  orderId: z.string(),
  orderName: z.string().nullable(),
  date: z.string(),
  // Why the reorder fired: initial setup, its own reorder point, a one-off, or
  // consolidation with something else already shipping.
  triggerType: z.enum(SuppliesReorderTriggerType),
  // What a consolidated reorder shipped alongside; empty for every other kind.
  consolidatedWith: z.array(ConsolidatedSiblingSchema),
  quantityOrderedCases: z.number(),
  // Available units when the reorder fired; null for initial setup and cart
  // purchases, which don't record it.
  availableInventoryAtTrigger: z.number().nullish(),
  fulfillmentStatus: z.string().nullable(),
  // Null until the reorder has been received/restocked.
  receivedDate: z.string().nullable(),
});

export type SuppliesInventoryReorderRow = z.infer<
  typeof SuppliesInventoryReorderRowSchema
>;

const SuppliesLabelInventoryChangeRowSchema = z.object({
  id: z.string(),
  date: z.string(),
  // Pretty batch id from the print job's fulfillment batch. Null when the row
  // has no print job (e.g. restocks) or the print job has only group ids.
  prettyBatchId: z.string().nullable(),
  // Signed inventory delta (negative = consumed, positive = restocked).
  delta: z.number(),
  balance: z.number(),
  notes: z.string().nullable(),
  referenceId: z.string().nullable(),
  userName: z.string().nullable(),
  // Deduped order names from the print job's batch / fulfillment groups.
  orderNames: z.array(z.string()),
  // Label PDF url from the print job; opens in a new tab.
  pdfUrl: z.string().nullable(),
});

export type SuppliesLabelInventoryChangeRow = z.infer<
  typeof SuppliesLabelInventoryChangeRowSchema
>;

const SuppliesParcelInventoryChangeRowSchema = z.object({
  id: z.string(),
  date: z.string(),
  // Fulfillment group id from the parcel-consume reference; drives the
  // "Go to shipment" link. Null for rows without a shipment (e.g. restocks).
  fulfillmentGroupId: z.string().nullable(),
  delta: z.number(),
  balance: z.number(),
  notes: z.string().nullable(),
  referenceId: z.string().nullable(),
  userName: z.string().nullable(),
  orderNames: z.array(z.string()),
  // OMS parcel name(s) tied to this config used on the shipment.
  parcelNames: z.array(z.string()),
});

export type SuppliesParcelInventoryChangeRow = z.infer<
  typeof SuppliesParcelInventoryChangeRowSchema
>;

export const getSuppliesInventoryConfigDetail = {
  name: "getSuppliesInventoryConfigDetail" as const,
  input: z.object({ configId: suppliesInventoryConfigIdFormat.schema }),
  output: z.object({
    config: SuppliesInventoryConfigRowSchema,
    // Ids needed to put this config's product in the supplies cart. Null when
    // the catalog product has no family (nothing to add), which also hides the
    // "Order more" action. Nullish so a pre-deploy server doesn't fail parsing.
    purchasable: z
      .object({
        productId: productIdFormat.schema,
        productFamilyId: productFamilyIdFormat.schema,
      })
      .nullish(),
    // OMS parcels tied to this config; empty for non-parcel configs.
    tiedParcels: z.array(
      z.object({
        name: z.string(),
        length: z.number(),
        width: z.number(),
        height: z.number(),
        lengthUnit: z.string(),
      }),
    ),
  }),
};

export const getSuppliesInventoryConfigReorders = {
  name: "getSuppliesInventoryConfigReorders" as const,
  input: z.object({
    configId: suppliesInventoryConfigIdFormat.schema,
    pageNumber: z.number(),
    pageSize: z.number(),
  }),
  output: z.object({ reorders: z.array(SuppliesInventoryReorderRowSchema) }),
};

export const getSuppliesInventoryConfigReordersCount = {
  name: "getSuppliesInventoryConfigReordersCount" as const,
  input: z.object({ configId: suppliesInventoryConfigIdFormat.schema }),
  output: z.object({ total: z.number() }),
};

export const getSuppliesInventoryConfigLabelHistory = {
  name: "getSuppliesInventoryConfigLabelHistory" as const,
  input: AdvancedFilterInputSchema.extend({
    configId: suppliesInventoryConfigIdFormat.schema,
    pageNumber: z.number(),
    pageSize: z.number(),
    opensearchPaginationCursor: OpensearchPaginationCursorSchema.nullish(),
  }),
  output: z.object({
    changes: z.array(SuppliesLabelInventoryChangeRowSchema),
    opensearchPaginationCursor: OpensearchPaginationCursorSchema.nullish(),
  }),
};

export const getSuppliesInventoryConfigInventoryHistoryCount = {
  name: "getSuppliesInventoryConfigInventoryHistoryCount" as const,
  input: AdvancedFilterInputSchema.extend({
    configId: suppliesInventoryConfigIdFormat.schema,
  }),
  output: z.object({ total: z.number() }),
};

// Parcel-packaging variant of the change history. Reuses the type-agnostic
// count RPC above (scoped by product + location).
export const getSuppliesInventoryConfigParcelHistory = {
  name: "getSuppliesInventoryConfigParcelHistory" as const,
  input: AdvancedFilterInputSchema.extend({
    configId: suppliesInventoryConfigIdFormat.schema,
    pageNumber: z.number(),
    pageSize: z.number(),
    opensearchPaginationCursor: OpensearchPaginationCursorSchema.nullish(),
  }),
  output: z.object({
    changes: z.array(SuppliesParcelInventoryChangeRowSchema),
    opensearchPaginationCursor: OpensearchPaginationCursorSchema.nullish(),
  }),
};

export const markSuppliesReorderReceived = {
  name: "markSuppliesReorderReceived" as const,
  input: z.object({ reorderId: suppliesInventoryReorderIdFormat.schema }),
  output: z.object({}),
};
